import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    FaBoxOpen, FaPlus, FaEdit, FaTrash, FaTimes,
    FaExclamationTriangle,
    FaLeaf, FaWeight, FaPercent, FaBarcode, FaSort,
} from "react-icons/fa";
import { resolveMediaUrl } from "../../helpers/media";
import PageHeader from "../../components/header/PageHeader";
import {
    getFPOCommodities,
    getFPOCommodityDropdown,
    addFPOCommodity,
    updateFPOCommodity,
    deleteFPOCommodity,
} from "../../services/fpoCommodity";
import { getUserRole } from "../../helpers/local_storage_helpers";
import SearchableSelect from "../../components/inputFields/SearchableSelect";

// ── GST options ───────────────────────────────────────────────────
const GST_OPTIONS = [0, 0.1, 0.25, 1, 1.5, 3, 5, 7.5, 12, 18, 28];

// ── Unit choices — must match model UNIT_CHOICES exactly ─────────
const UNIT_CHOICES = [
    { value: "qtl", label: "Quintal" },
    { value: "kg",  label: "Kg" },
    { value: "ton", label: "Ton" },
    { value: "bag", label: "Bag" },
];

// ── Validation Schema ─────────────────────────────────────────────
const CommoditySchema = Yup.object().shape({
    commodity_id:              Yup.string().required("Please select a commodity"),
    unit:                      Yup.string().oneOf(["qtl","kg","ton","bag"]).required("Unit is required"),
    hsn_code:                  Yup.string().max(10, "HSN code max 10 characters").nullable(),
    default_gst_percent:       Yup.number()
        .min(0, "Min 0%").max(28, "Max 28%")
        .required("GST % is required"),
    standard_moisture_percent: Yup.number()
        .min(0, "Min 0%").max(100, "Max 100%").nullable()
        .transform(v => (isNaN(v) ? null : v)),
    low_stock_threshold:       Yup.number()
        .min(0, "Must be ≥ 0").nullable()
        .transform(v => (isNaN(v) ? null : v)),
    display_order:             Yup.number()
        .integer("Must be a whole number").min(0).nullable()
        .transform(v => (isNaN(v) ? null : v)),
    is_active:                 Yup.boolean(),
});

// ── Pagination ───────────────────────────────────────────────────
const PAGE_SIZE = 24;

const Pagination = ({ page, total, pageSize, onPage }) => {
    const pages = Math.ceil(total / pageSize) || 1;
    if (pages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-1 py-3 mt-2">
            <p className="text-xs text-gray-400">
                Showing {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onPage(page - 1)}
                    className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                    Previous
                </button>
                <span className="text-xs text-gray-600 px-2">{page} / {pages}</span>
                <button
                    type="button"
                    disabled={page >= pages}
                    onClick={() => onPage(page + 1)}
                    className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

// ── Modal Shell ───────────────────────────────────────────────────
const Modal = ({ title, icon: Icon, onClose, children, wide }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            onClick={(e) => e.stopPropagation()}
            className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
        >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="text-primary text-sm" />
                    </div>
                    <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg transition-colors">
                    <FaTimes />
                </button>
            </div>
            <div className="p-5">{children}</div>
        </motion.div>
    </motion.div>
);

// ── Form Field wrapper ────────────────────────────────────────────
const FieldWrap = ({ label, name, required, hint, children }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            {hint && <span className="ml-1 text-gray-400 font-normal">{hint}</span>}
        </label>
        {children}
        <ErrorMessage name={name} component="p" className="text-red-500 text-xs mt-1" />
    </div>
);

// ── Status Badge ──────────────────────────────────────────────────
const StatusBadge = ({ active }) => (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full
        ${active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-green-500" : "bg-gray-400"}`} />
        {active ? "Active" : "Inactive"}
    </span>
);

// ── Status Toggle Bar (Active/Inactive switch) ────────────────────
const StatusToggle = ({ active, onToggle }) => (
    <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2"
        title={active ? "Deactivate" : "Activate"}
    >
        <span className={`text-[10px] font-bold ${active ? "text-green-600" : "text-gray-400"}`}>
            {active ? "Active" : "Inactive"}
        </span>
        <span className={`relative w-9 h-5 rounded-full transition-colors duration-200
            ${active ? "bg-primary" : "bg-gray-300"}`}>
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200
                ${active ? "translate-x-4" : "translate-x-0"}`} />
        </span>
    </button>
);

// ── Commodity Card (Flip) ─────────────────────────────────────────
const CommodityCard = ({ item, isAdmin, onEdit, onDelete, onToggle }) => {
    const [flipped, setFlipped]     = useState(false);
    const [imgFailed, setImgFailed] = useState(false);
    const imgUrl = resolveMediaUrl(item.commodity?.image);

    const fmt = (val) => {
        if (val == null || val === "") return "—";
        const n = parseFloat(val);
        return isNaN(n) ? val : (n % 1 === 0 ? n.toString() : n.toFixed(2).replace(/\.?0+$/, ""));
    };

    const stockLow = item.low_stock_threshold != null
        && parseFloat(item.current_stock) <= parseFloat(item.low_stock_threshold);

    const stopAndEdit   = (e) => { e.stopPropagation(); onEdit(item); };
    const stopAndToggle = (e) => { e.stopPropagation(); onToggle(item); };
    const stopAndDelete = (e) => { e.stopPropagation(); onDelete(item); };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="cursor-pointer"
            style={{ perspective: "1000px", height: "260px" }}
            onClick={() => setFlipped(f => !f)}
        >
            {/* Inner rotating container */}
            <div
                style={{
                    position:       "relative",
                    width:          "100%",
                    height:         "100%",
                    transformStyle: "preserve-3d",
                    transition:     "transform 0.5s cubic-bezier(0.4,0.2,0.2,1)",
                    transform:      flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
            >
                {/* ── FRONT ── */}
                <div
                    style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
                    className={`absolute inset-0 bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col
                        ${item.is_active ? "border-gray-100" : "border-gray-200 opacity-80"}`}
                >
                    {/* Image area */}
                    <div className="flex-1 relative bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center overflow-hidden">
                        {imgUrl && !imgFailed ? (
                            <img
                                src={imgUrl}
                                alt={item.commodity?.name}
                                className="w-full h-full object-cover"
                                onError={() => setImgFailed(true)}
                            />
                        ) : (
                            <FaLeaf className="text-primary/30 text-6xl" />
                        )}
                        {/* Status badge top-right */}
                        <div className="absolute top-2.5 right-2.5">
                            <StatusBadge active={item.is_active} />
                        </div>
                        {/* Stock badge bottom-left */}
                        <div className={`absolute bottom-2.5 left-2.5 px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1
                            ${stockLow ? "bg-red-500 text-white" : "bg-white/90 text-gray-700 border border-gray-100"}`}>
                            {stockLow && <FaExclamationTriangle size={9} />}
                            {fmt(item.current_stock)} {item.unit}
                        </div>
                    </div>

                    {/* Name / type footer */}
                    <div className="px-4 py-3 border-t border-gray-50 flex items-center justify-between">
                        <div className="min-w-0">
                            <p className="font-bold text-gray-800 text-sm truncate">{item.commodity?.name || "—"}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{item.commodity?.type || "—"}</p>
                        </div>
                        <span className="text-[10px] text-gray-300 ml-2 flex-shrink-0">Tap to flip →</span>
                    </div>
                </div>

                {/* ── BACK ── */}
                <div
                    style={{
                        backfaceVisibility:       "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform:                "rotateY(180deg)",
                    }}
                    className={`absolute inset-0 bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col
                        ${item.is_active ? "border-gray-100" : "border-gray-200 opacity-80"}`}
                >
                    {/* Back header */}
                    <div className="px-4 pt-4 pb-3 border-b border-gray-50 flex items-center justify-between">
                        <div>
                            <p className="font-bold text-gray-800 text-sm">{item.commodity?.name || "—"}</p>
                            <p className="text-[10px] text-gray-400">{item.commodity?.type || "—"}</p>
                        </div>
                        {isAdmin ? (
                            <StatusToggle active={item.is_active} onToggle={stopAndToggle} />
                        ) : (
                            <StatusBadge active={item.is_active} />
                        )}
                    </div>

                    {/* Detail rows */}
                    <div className="flex-1 px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2.5 content-start">
                        <InfoCell icon={FaWeight}              label="Unit"          value={item.unit || "—"} />
                        <InfoCell icon={FaPercent}             label="GST"           value={`${fmt(item.default_gst_percent)}%`} />
                        <InfoCell icon={FaBarcode}             label="HSN Code"      value={item.hsn_code || "—"} />
                        <InfoCell icon={FaPercent}             label="Moisture"      value={item.standard_moisture_percent != null ? `${fmt(item.standard_moisture_percent)}%` : "—"} />
                        <InfoCell icon={FaExclamationTriangle} label="Low Threshold" value={item.low_stock_threshold != null ? `${fmt(item.low_stock_threshold)} ${item.unit || ""}` : "—"} />
                        <InfoCell icon={FaSort}                label="Display Order" value={item.display_order ?? "—"} />
                    </div>

                    {/* Actions */}
                    <div className="px-4 pb-4 pt-2 border-t border-gray-50 flex items-center gap-2">
                        {isAdmin ? (
                            <>
                                <button
                                    onClick={stopAndEdit}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors"
                                >
                                    <FaEdit size={10} /> Edit
                                </button>
                                <button
                                    onClick={stopAndDelete}
                                    className="flex items-center justify-center w-9 h-9 rounded-xl text-red-400 bg-red-50 hover:bg-red-100 transition-colors"
                                    title="Remove"
                                >
                                    <FaTrash size={10} />
                                </button>
                            </>
                        ) : (
                            <p className="text-xs text-gray-300 w-full text-center">Tap card to flip back</p>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const InfoCell = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2">
        <div className="w-5 h-5 rounded-md bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon className="text-gray-400 text-[9px]" />
        </div>
        <div>
            <p className="text-[10px] text-gray-400 font-medium">{label}</p>
            <p className="text-xs font-semibold text-gray-700">{value}</p>
        </div>
    </div>
);


// ── Main Component ────────────────────────────────────────────────
const FPOCommodity = () => {
    const [commodities, setCommodities] = useState([]);
    const [total, setTotal]             = useState(0);
    const [page, setPage]               = useState(1);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);

    // Dropdown of global commodities (for Add / Edit select)
    const [commodityOptions, setCommodityOptions] = useState([]);
    const [optionsLoading, setOptionsLoading]      = useState(false);

    // Modal state
    const [showAddModal, setShowAddModal]     = useState(false);
    const [editingItem, setEditingItem]       = useState(null);
    const [deletingItem, setDeletingItem]     = useState(null);
    const [deleting, setDeleting]             = useState(false);

    // Filter state
    const [search, setSearch]         = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [activeFilter, setActiveFilter] = useState("all");

    // Primary FPO owner logins carry no "role" (only sub-staff accounts do) — treat that as admin too
    const userRole = getUserRole();
    const isAdmin  = userRole === "fpo_admin" || !userRole;

    // debounce search so we don't fire a request on every keystroke
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);
    useEffect(() => { setPage(1); }, [debouncedSearch, typeFilter, activeFilter]);

    // ── Fetch ─────────────────────────────────────────────────────
    const loadCommodities = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch) params.search = debouncedSearch;
            if (typeFilter !== "all") params.type = typeFilter;
            if (activeFilter !== "all") params.is_active = activeFilter === "active";
            const res  = await getFPOCommodities(params);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                const results = data.results || data.data || [];
                setCommodities(results);
                setTotal(data.count ?? results.length);
            } else {
                setError(data?.message || "Failed to load commodities.");
            }
        } catch {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, typeFilter, activeFilter]);

    // ── Fetch dropdown options ─────────────────────────────────────
    const loadCommodityOptions = async () => {
        setOptionsLoading(true);
        try {
            const res  = await getFPOCommodityDropdown();
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                setCommodityOptions(data.data || []);
            }
        } catch {
            // silently ignore — select will just show no options
        } finally {
            setOptionsLoading(false);
        }
    };

    useEffect(() => { loadCommodities(); }, [loadCommodities]);
    useEffect(() => { loadCommodityOptions(); }, []);

    // ── Unique commodity types for filter — derived from whatever page is loaded
    //    (the list is now server-paginated, so this may not cover every type
    //    until you've paged through, or search/filter down to find it) ─
    const commodityTypes = useMemo(() => {
        const types = [...new Set(commodities.map(c => c.commodity?.type).filter(Boolean))];
        return types.sort();
    }, [commodities]);

    // page already reflects search/type/active filters from the server
    const filtered = commodities;

    // ── Add / Edit submit ─────────────────────────────────────────
    const handleSubmit = async (values, { resetForm }) => {
        try {
            const payload = {
                commodity:                 values.commodity_id,
                unit:                      values.unit,
                hsn_code:                  values.hsn_code || null,
                default_gst_percent:       parseFloat(values.default_gst_percent),
                standard_moisture_percent: values.standard_moisture_percent !== "" ? parseFloat(values.standard_moisture_percent) : null,
                low_stock_threshold:       values.low_stock_threshold !== "" ? parseFloat(values.low_stock_threshold) : null,
                display_order:             values.display_order !== "" ? parseInt(values.display_order, 10) : 0,
                is_active:                 values.is_active,
            };

            let res;
            if (editingItem) {
                res = await updateFPOCommodity(editingItem.id, payload);
            } else {
                res = await addFPOCommodity(payload);
            }

            const data = res?.data;
            const ok   = data?.status === 200 || data?.status === 201
                || data?.status === "200" || data?.status === "201"
                || res?.status === 200 || res?.status === 201;

            if (ok) {
                toast.success(editingItem ? "Commodity updated! ✅" : "Commodity added! ✅");
                resetForm();
                setEditingItem(null);
                setShowAddModal(false);
                await loadCommodities();
            } else {
                const errs = data?.errors;
                const msg  = errs ? Object.values(errs).flat().join(", ") : (data?.message || "Operation failed.");
                toast.error(msg);
            }
        } catch (e) {
            const errs = e?.response?.data?.errors;
            const msg  = errs ? Object.values(errs).flat().join(", ") : (e?.response?.data?.message || "Something went wrong.");
            toast.error(msg);
        }
    };

    // ── Toggle active ─────────────────────────────────────────────
    const handleToggle = async (item) => {
        try {
            const res  = await updateFPOCommodity(item.id, { is_active: !item.is_active });
            const data = res?.data;
            const ok   = data?.status === 200 || data?.status === "200" || res?.status === 200;
            if (ok) {
                toast.success(`Commodity ${!item.is_active ? "activated" : "deactivated"}`);
                await loadCommodities();
            } else {
                toast.error(data?.message || "Failed to update status.");
            }
        } catch {
            toast.error("Something went wrong.");
        }
    };

    // ── Delete ────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deletingItem) return;
        setDeleting(true);
        try {
            const res  = await deleteFPOCommodity(deletingItem.id);
            const data = res?.data;
            const ok   = data?.status === 200 || data?.status === "200" || res?.status === 200 || res?.status === 204;
            if (ok) {
                toast.success("Commodity removed.");
                setDeletingItem(null);
                await loadCommodities();
            } else {
                toast.error(data?.message || "Failed to delete.");
            }
        } catch {
            toast.error("Something went wrong.");
        } finally {
            setDeleting(false);
        }
    };

    // ── Initial values for the form ───────────────────────────────
    const getInitialValues = () => {
        if (editingItem) {
            return {
                commodity_id:              editingItem.commodity?.id || "",
                unit:                      editingItem.unit || "",
                hsn_code:                  editingItem.hsn_code || "",
                default_gst_percent:       editingItem.default_gst_percent != null ? Number(editingItem.default_gst_percent) : 0,
                standard_moisture_percent: editingItem.standard_moisture_percent ?? "",
                low_stock_threshold:       editingItem.low_stock_threshold ?? "",
                display_order:             editingItem.display_order ?? 0,
                is_active:                 editingItem.is_active ?? true,
            };
        }
        return {
            commodity_id:              "",
            unit:                      "kg",
            hsn_code:                  "",
            default_gst_percent:       0,
            standard_moisture_percent: "",
            low_stock_threshold:       "",
            display_order:             0,
            is_active:                 true,
        };
    };

    // ── Render ────────────────────────────────────────────────────
    return (
        <div className="w-full pb-12">

            {/* ── Page Header ── */}
            <PageHeader
                title="Commodities"
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search by name, type, HSN, unit…"
                actionLabel="Add Commodity"
                actionIcon={FaPlus}
                onAction={() => { setEditingItem(null); setShowAddModal(true); }}
            />
            <div className="flex items-center gap-2 mb-3 px-1">
                <span className="text-[11px] text-gray-500">{total} total</span>
            </div>

            {/* ── Filters ── */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-5 flex flex-col sm:flex-row gap-3"
            >
                {/* Type Filter */}
                <div className="w-full sm:w-48">
                    <SearchableSelect
                        value={typeFilter}
                        onChange={setTypeFilter}
                        options={[{ value: "all", label: "All Types" }, ...commodityTypes.map(t => ({ value: t, label: t }))]}
                        className="pl-3 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50"
                    />
                </div>

                {/* Status Filter */}
                <div className="w-full sm:w-48">
                    <SearchableSelect
                        value={activeFilter}
                        onChange={setActiveFilter}
                        options={[
                            { value: "all", label: "All Status" },
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                        ]}
                        className="pl-3 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50"
                    />
                </div>
            </motion.div>

            {/* ── Content ── */}
            {loading ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 text-sm">Loading commodities…</p>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <p className="text-red-500 font-medium">{error}</p>
                    <button onClick={loadCommodities} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Retry</button>
                </div>
            ) : filtered.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center h-56 gap-3 text-center"
                >
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <FaBoxOpen className="text-gray-300 text-3xl" />
                    </div>
                    <p className="font-semibold text-gray-500 text-sm">
                        {commodities.length === 0 ? "No commodities configured yet" : "No results match your filters"}
                    </p>
                    {commodities.length === 0 && (
                        <button
                            onClick={() => { setEditingItem(null); setShowAddModal(true); }}
                            className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold flex items-center gap-2"
                        >
                            <FaPlus size={11} /> Add First Commodity
                        </button>
                    )}
                </motion.div>
            ) : (
                <motion.div
                    layout
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                    <AnimatePresence>
                        {filtered.map(item => (
                            <CommodityCard
                                key={item.id}
                                item={item}
                                isAdmin={isAdmin}
                                onEdit={(c) => { setEditingItem(c); setShowAddModal(true); }}
                                onDelete={setDeletingItem}
                                onToggle={handleToggle}
                            />
                        ))}
                    </AnimatePresence>
                </motion.div>
            )}
            {!loading && !error && <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />}

            {/* ── Add / Edit Modal ── */}
            <AnimatePresence>
                {showAddModal && (
                    <Modal
                        title={editingItem ? `Edit – ${editingItem.commodity?.name}` : "Add Commodity"}
                        icon={editingItem ? FaEdit : FaPlus}
                        onClose={() => { setShowAddModal(false); setEditingItem(null); }}
                        wide
                    >
                        <Formik
                            key={editingItem?.id || "new"}
                            initialValues={getInitialValues()}
                            validationSchema={CommoditySchema}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting, values, setFieldValue }) => (
                                <Form className="flex flex-col gap-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                                        {/* Commodity — select from global dropdown */}
                                        <FieldWrap name="commodity_id" label="Commodity" required>
                                            <SearchableSelect
                                                value={values.commodity_id}
                                                onChange={(v) => setFieldValue("commodity_id", v)}
                                                disabled={optionsLoading}
                                                placeholder={optionsLoading ? "Loading commodities…" : "— Select commodity —"}
                                                searchPlaceholder="Search commodities…"
                                                options={commodityOptions.map(c => ({ value: c.id, label: c.name }))}
                                                className="px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-60"
                                            />
                                        </FieldWrap>

                                        {/* Unit — matches model UNIT_CHOICES */}
                                        <FieldWrap name="unit" label="Unit" required>
                                            <SearchableSelect
                                                value={values.unit}
                                                onChange={(v) => setFieldValue("unit", v)}
                                                placeholder="— Select unit —"
                                                options={UNIT_CHOICES}
                                                className="px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white"
                                            />
                                        </FieldWrap>

                                        {/* GST */}
                                        <FieldWrap name="default_gst_percent" label="Default GST %" required>
                                            <SearchableSelect
                                                value={values.default_gst_percent}
                                                onChange={(v) => setFieldValue("default_gst_percent", v)}
                                                options={GST_OPTIONS.map(g => ({ value: g, label: `${g}%` }))}
                                                className="px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white"
                                            />
                                        </FieldWrap>

                                        {/* HSN Code — max_length=10 per model */}
                                        <FieldWrap name="hsn_code" label="HSN Code" hint="(optional, max 10 chars)">
                                            <Field
                                                name="hsn_code"
                                                maxLength={10}
                                                placeholder="e.g. 10019910"
                                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                                            />
                                        </FieldWrap>

                                        {/* Standard Moisture */}
                                        <FieldWrap name="standard_moisture_percent" label="Standard Moisture %" hint="(optional)">
                                            <Field
                                                name="standard_moisture_percent"
                                                type="number" min="0" max="100" step="0.01"
                                                placeholder="e.g. 14"
                                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                                            />
                                        </FieldWrap>

                                        {/* Low Stock Threshold */}
                                        <FieldWrap name="low_stock_threshold" label="Low Stock Threshold" hint="(optional)">
                                            <Field
                                                name="low_stock_threshold"
                                                type="number" min="0" step="0.01"
                                                placeholder="e.g. 100"
                                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                                            />
                                        </FieldWrap>

                                        {/* Display Order */}
                                        <FieldWrap name="display_order" label="Display Order" hint="(optional)">
                                            <Field
                                                name="display_order"
                                                type="number" min="0" step="1"
                                                placeholder="e.g. 1"
                                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                                            />
                                        </FieldWrap>

                                        {/* Is Active toggle */}
                                        <div className="flex items-center gap-3 pt-5">
                                            <button
                                                type="button"
                                                onClick={() => setFieldValue("is_active", !values.is_active)}
                                                className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none
                                                    ${values.is_active ? "bg-primary" : "bg-gray-300"}`}
                                            >
                                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200
                                                    ${values.is_active ? "translate-x-5" : "translate-x-0"}`} />
                                            </button>
                                            <span className="text-sm font-semibold text-gray-700">
                                                {values.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Submit */}
                                    <div className="flex gap-3 pt-2 border-t border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => { setShowAddModal(false); setEditingItem(null); }}
                                            className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-1 py-2.5 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                        >
                                            {isSubmitting ? "Saving…" : (editingItem ? "Update Commodity" : "Add Commodity")}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Modal>
                )}
            </AnimatePresence>

            {/* ── Delete Confirm Modal ── */}
            <AnimatePresence>
                {deletingItem && (
                    <Modal
                        title="Remove Commodity"
                        icon={FaTrash}
                        onClose={() => setDeletingItem(null)}
                    >
                        <div className="flex flex-col items-center gap-4 text-center">
                            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                                <FaExclamationTriangle className="text-red-400 text-2xl" />
                            </div>
                            <div>
                                <p className="font-bold text-gray-800">Remove <span className="text-primary">"{deletingItem.commodity?.name}"</span>?</p>
                                <p className="text-gray-500 text-xs mt-1">
                                    This will remove this commodity configuration from your FPO. This action cannot be undone.
                                </p>
                            </div>
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setDeletingItem(null)}
                                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-60"
                                >
                                    {deleting ? "Removing…" : "Yes, Remove"}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FPOCommodity;
